import React, { useEffect, useRef, useState } from "react";
import Layout from "../../components/Layout";
import { getEmployeeDetails } from "../../api/EmployeeApi";
import {
  getAllParcelAddressbyRoute,
  addParcelStreet,
  updateParcelStreet,
  getAllStreetbyRoute,
} from "../../api/RouteApi";
import ToastFile from "../../utils/ToastFile";
import { Button, Form, Modal, Table } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import ReactSelect from "react-select";
import { useNavigate, useLocation } from "react-router-dom";
import ButtonLoader from "../../components/ButtonLoader";
import { ORDER_TYPES } from "../../constants/orderTypes";

const AddRouteAddress = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  const fetched = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const routeId = location.state?.routeId;
  const editData = location.state?.editData;
  let lastOrder = location.state?.lastOrder || 0;
  const isEdit = !!editData;
  const [streetOption, setStreetOption] = useState([]);
  const [selectedStreet, setSelectedStreet] = useState(null);
  const [isLoading, setIsloading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;

    if (routeId) {
      fetchParcelStreetByRoute(routeId);
    }

    // Pre-populate form for edit
    if (editData) {
      setValue("startNumber", editData.homeStart);
      setValue("secondNumber", editData.homeEnd);
      setValue("orderType", editData.sequenceType);
      setSelectedStreet({ value: editData._id, label: editData.streetName });
      setValue("searchStreet", {
        value: editData._id,
        label: editData.streetName,
      });
    }
  }, [routeId]);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
  } = useForm();

  const onSubmit = async (data) => {
    // Validation 1: Street selection
    if (!selectedStreet) {
      ToastFile("Please select a street", "error");
      return;
    }
    
    // Validation 2: Start and end numbers
    if (!data.startNumber || !data.secondNumber) {
      ToastFile("Please enter both start and end numbers", "error");
      return;
    }
    
    // Validation 3: Start number must be less than end number
    if (parseInt(data.startNumber) >= parseInt(data.secondNumber)) {
      ToastFile("Start number must be less than end number", "error");
      return;
    }
    
    // Validation 4: Order type selection
    if (!data.orderType) {
      ToastFile("Please select a number order type", "error");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        streetName: selectedStreet.label,
        homeStart: parseInt(data.startNumber),
        homeEnd: parseInt(data.secondNumber),
        sequenceType: data.orderType,
        route_id: routeId,
        emp_id: user?.user_id,
        order: isEdit ? lastOrder : lastOrder + 1,
        tenant_id: user.tenant_id,
        createdBy: user?.user_id,
        createdAt: new Date().toISOString(),
        modifiedBy: user?.user_id,
        modifiedAt: new Date().toISOString(),
      };

      const res = isEdit
        ? await updateParcelStreet(editData._id, payload)
        : await addParcelStreet(payload);

      if (res.status === 200 || res.status === 201) {
        ToastFile(
          `Street ${isEdit ? "updated" : "added"} successfully`,
          "success",
        );
        navigate("/generateParcelAddress");
      } else {
        ToastFile(`Failed to ${isEdit ? "update" : "add"} street`, "error");
      }
    } catch (err) {
      console.error(`Error ${isEdit ? "updating" : "adding"} street:`, err);
      ToastFile(`Error ${isEdit ? "updating" : "adding"} street`, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchParcelStreetByRoute = async (routeId) => {
    setIsloading(true);
    try {
      const res = await getAllStreetbyRoute(
        routeId
      );
      if (res.status === 200) {
        const options = Array.from(
          new Map(
            res?.data?.data?.streets.map((parcel) => [
              parcel.streetName, // key (unique)
              {
                value: parcel.streetName,
                label: parcel.streetName,
              },
            ]),
          ).values(),
        );
        setStreetOption(options);
      } else {
        ToastFile("Failed to fetch address", "error");
      }
    } catch (err) {
      console.error("Error fetching address:", err);
      ToastFile("Error fetching address", "error");
    } finally {
      setIsloading(false);
    }
  };

  return (
    <Layout>
      <div className="container-fluid">
        <div className="row justify-content-center">
          <div className="col-12 col-md-8 col-lg-6">
            <div className="card shadow-sm">
              <div className="card-header bg-dark text-white text-center">
                 <span
            style={{
              fontSize: "22px",
              fontWeight: 600,
              minWidth: "140px",
            }}
          >
            {isEdit ? "Edit Street" : "Add Street"}
          </span>
              </div>
              <div className="card-body p-4">
                <Form
                  onSubmit={handleSubmit(onSubmit)}
                  className="d-flex flex-column gap-4"
                >
                  <Form.Group>
                    <Form.Label className="fw-semibold mb-2">
                      Search Street
                    </Form.Label>
                    <Controller
                      control={control}
                      name="searchStreet"
                      rules={{ required: "Please select a street" }}
                      render={({ field }) => (
                        <ReactSelect
                          {...field}
                          className={errors.searchStreet ? "is-invalid" : ""}
                          options={streetOption}
                          placeholder="Select a street..."
                          isLoading={isLoading}
                          onChange={(selected) => {
                            field.onChange(selected);
                            setSelectedStreet(selected);
                          }}
                        />
                      )}
                    />
                    {errors.searchStreet && (
                      <div className="text-danger small mt-1">
                        {errors.searchStreet.message}
                      </div>
                    )}
                  </Form.Group>

                  <Form.Group>
                    <Form.Label className="fw-semibold mb-2">
                      Select Range
                    </Form.Label>
                    <div className="d-flex gap-3 align-items-center">
                      <Form.Control
                        type="number"
                        {...register("startNumber", { 
                          required: "Start number is required",
                          min: { value: 1, message: "Start number must be at least 1" }
                        })}
                        placeholder="Start"
                        className={errors.startNumber ? "is-invalid" : ""}
                        style={{ maxWidth: "120px" }}
                      />
                      <span className="fw-bold">to</span>
                      <Form.Control
                        type="number"
                        {...register("secondNumber", { 
                          required: "End number is required",
                          min: { value: 1, message: "End number must be at least 1" }
                        })}
                        placeholder="End"
                        className={errors.secondNumber ? "is-invalid" : ""}
                        style={{ maxWidth: "120px" }}
                      />
                    </div>
                  </Form.Group>

                  <Form.Group>
                    <Form.Label className="fw-semibold mb-3">
                      Number Order Type
                    </Form.Label>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="d-flex flex-column gap-2">
                          {ORDER_TYPES.slice(0, 3).map((type) => (
                            <Form.Check
                              key={type.id}
                              type="radio"
                              id={type.id}
                              label={type.label}
                              value={type.id}
                              {...register("orderType", { required: "Please select a number order type" })}
                            />
                          ))}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="d-flex flex-column gap-2">
                          {ORDER_TYPES.slice(3).map((type) => (
                            <Form.Check
                              key={type.id}
                              type="radio"
                              id={type.id}
                              label={type.label}
                              value={type.id}
                              {...register("orderType")}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                    {errors.orderType && (
                      <div className="text-danger small mt-2">
                        {errors.orderType.message}
                      </div>
                    )}
                    {(errors.startNumber || errors.secondNumber) && (
                      <div className="text-danger small mt-1">
                        {errors.startNumber?.message || errors.secondNumber?.message}
                      </div>
                    )}
                  </Form.Group>

                  <div className="d-flex justify-content-center gap-3 mt-4">
                    <Button
                      variant="secondary"
                      onClick={() => navigate("/generateParcelAddress")}
                      style={{ minWidth: "100px" }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={isSubmitting}
                      style={{ minWidth: "100px" }}
                    >
                      {isSubmitting
                        ? isEdit
                          ? "Updating..."
                          : "Adding..."
                        : isEdit
                          ? "Update Street"
                          : "Add Street"}
                    </Button>
                  </div>
                </Form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AddRouteAddress;
